import React, { useState } from 'react'
import { usePartnerAuth } from '../../context/PartnerAuthContext'
import { supabase } from '../../lib/supabaseClient'

const styles = {
  card: {
    background: 'white',
    border: '1px solid #e2e8f0',
    borderRadius: '0.75rem',
    padding: '2rem',
    maxWidth: '500px',
  } as React.CSSProperties,
  title: {
    fontSize: '1.3rem',
    fontWeight: '600',
    color: '#1e293b',
    marginBottom: '1.5rem',
    paddingBottom: '0.75rem',
    borderBottom: '1px solid #e2e8f0',
  } as React.CSSProperties,
  formGroup: {
    marginBottom: '1.25rem',
  } as React.CSSProperties,
  label: {
    display: 'block',
    marginBottom: '0.3rem',
    fontWeight: '500',
    fontSize: '0.9rem',
    color: '#334155',
  } as React.CSSProperties,
  input: {
    width: '100%',
    padding: '0.5rem 0.75rem',
    border: '1px solid #cbd5e1',
    borderRadius: '0.375rem',
    fontSize: '0.9rem',
    boxSizing: 'border-box' as const,
  } as React.CSSProperties,
  button: {
    padding: '0.65rem 1.5rem',
    background: '#3b82f6',
    color: 'white',
    border: 'none',
    borderRadius: '0.375rem',
    fontSize: '0.95rem',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'all 0.2s',
  } as React.CSSProperties,
  buttonDisabled: {
    padding: '0.65rem 1.5rem',
    background: '#94a3b8',
    color: 'white',
    border: 'none',
    borderRadius: '0.375rem',
    fontSize: '0.95rem',
    fontWeight: '500',
    cursor: 'not-allowed',
  } as React.CSSProperties,
  error: {
    background: '#fef2f2',
    border: '1px solid #fecaca',
    color: '#dc2626',
    padding: '0.75rem 1rem',
    borderRadius: '0.375rem',
    fontSize: '0.9rem',
    marginBottom: '1rem',
  } as React.CSSProperties,
  success: {
    background: '#f0fdf4',
    border: '1px solid #bbf7d0',
    color: '#16a34a',
    padding: '0.75rem 1rem',
    borderRadius: '0.375rem',
    fontSize: '0.9rem',
    marginBottom: '1rem',
  } as React.CSSProperties,
}

const PartnerChangePasswordPage: React.FC = () => {
  const { partnerProfile } = usePartnerAuth()

  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)

  const validate = (): string | null => {
    if (!currentPassword) return 'Veuillez saisir votre mot de passe actuel.'
    if (newPassword.length < 6) return 'Le nouveau mot de passe doit contenir au moins 6 caractères.'
    if (newPassword !== confirmPassword) return 'Les deux mots de passe ne correspondent pas.'
    return null
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setSuccess(false)

    const validationError = validate()
    if (validationError) {
      setError(validationError)
      return
    }

    setLoading(true)
    try {
      // Vérifier l'ancien mot de passe en se ré-authentifiant
      const { error: signInError } = await supabase.auth.signInWithPassword({
        email: partnerProfile!.email,
        password: currentPassword,
      })

      if (signInError) {
        setError('Le mot de passe actuel est incorrect.')
        setLoading(false)
        return
      }

      // Mettre à jour le mot de passe
      const { error: updateError } = await supabase.auth.updateUser({
        password: newPassword,
      })

      if (updateError) {
        setError(`Erreur lors de la mise à jour : ${updateError.message}`)
        setLoading(false)
        return
      }

      setSuccess(true)
      setCurrentPassword('')
      setNewPassword('')
      setConfirmPassword('')
    } catch {
      setError('Une erreur inattendue est survenue.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div>
      <h2 style={{ fontSize: '1.5rem', fontWeight: '600', color: '#1e293b', marginBottom: '1.5rem' }}>
        🔒 Changer le mot de passe
      </h2>

      <div style={styles.card}>
        <h3 style={styles.title}>Modifier votre mot de passe</h3>

        {error && (
          <div style={styles.error} data-testid="password-error">
            {error}
          </div>
        )}

        {success && (
          <div style={styles.success} data-testid="password-success">
            Mot de passe modifié avec succès !
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div style={styles.formGroup}>
            <label style={styles.label} htmlFor="current-password">
              Mot de passe actuel
            </label>
            <input
              id="current-password"
              data-testid="current-password"
              type="password"
              style={styles.input}
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              autoComplete="current-password"
            />
          </div>

          <div style={styles.formGroup}>
            <label style={styles.label} htmlFor="new-password">
              Nouveau mot de passe (min. 6 caractères)
            </label>
            <input
              id="new-password"
              data-testid="new-password"
              type="password"
              style={styles.input}
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              autoComplete="new-password"
            />
          </div>

          <div style={styles.formGroup}>
            <label style={styles.label} htmlFor="confirm-password">
              Confirmer le nouveau mot de passe
            </label>
            <input
              id="confirm-password"
              data-testid="confirm-password"
              type="password"
              style={styles.input}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              autoComplete="new-password"
            />
          </div>

          <button
            type="submit"
            data-testid="submit-password"
            style={loading ? styles.buttonDisabled : styles.button}
            disabled={loading}
          >
            {loading ? 'Modification en cours…' : 'Modifier le mot de passe'}
          </button>
        </form>
      </div>
    </div>
  )
}

export default PartnerChangePasswordPage
